import { useEffect, useRef, useState } from 'react';
import { Link, Outlet, useMatch, useOutletContext, useParams } from 'react-router-dom';
import { TabLink } from '../../components/ui/scroll-reset';
import type { ImportSession } from '@ni/shared';
import {
  useCancelImport,
  useImportPhaseInvalidation,
  useImportSession,
  useOpenImport,
  usePreviewImport,
  useReadImportNotes,
} from '../../api/hooks';
import { isNotFound, loadErrorLine } from '../../api/errors';
import {
  IMPORT_CANCEL,
  IMPORT_CANCEL_LINE,
  IMPORT_BANNER_QUESTION,
  IMPORT_BANNER_READY,
  IMPORT_BANNER_REVIEW_FINDINGS,
  IMPORT_BANNER_TO_IMPORT,
  IMPORT_BANNER_TO_PEOPLE,
  importBannerNeedsLook,
  IMPORT_CANCELLING,
  IMPORT_CHAT_HEAD,
  IMPORT_KEEP,
  IMPORT_OPENING_TURN,
  IMPORT_PREVIEW_BAR_NEVER,
  IMPORT_PREVIEW_BAR_RUNNING,
  IMPORT_PREVIEW_BAR_STALE,
  IMPORT_PREVIEW_RUN,
  IMPORT_PREVIEW_RUNNING,
  IMPORT_READ_THE_REST,
  IMPORT_READING_THE_REST,
  IMPORT_RETRIED_FROM,
  IMPORT_RETRIED_FROM_LINK,
  IMPORT_TAB_LABELS,
  importHeadLine,
  importPausedBannerLine,
  importProgressLine,
  IMPORTS_TITLE,
  importWaitingBannerLine,
  NOT_FOUND,
  NOT_FOUND_LINE,
} from '../../lib/copy';
import { ImportChat, type OpeningTurn } from '../../components/imports/import-chat';
import { ErrorLine } from '../../components/ui/error-line';
import { DangerLink } from '../../components/ui/button';
import { ConfirmModal } from '../../components/ui/confirm-modal';
import { WorkingButton } from '../../components/ui/working-button';
import { needsOpeningTurn, openingTurnState } from './open-turn';
import { sessionBannerState, type SessionBannerState } from './preview-bar';
import { Loading } from '../../components/ui/loading';
import { EmptyLine } from '../../components/ui/empty-line';
import './preview-bar.css';
import './import.css';
import { TopBar } from '../../components/ui/top-bar';
import { ImportEventBadge } from '../imports';

/**
 * **The session page** (import note §2.4): the conversation on the left, four
 * tabs on the right, and one line at the top saying where the whole thing is.
 *
 * The page is **one read**. `GET /imports/:id` carries the transcript, the plan,
 * the version chain and every count, so a reload rebuilds the page rather than
 * fetching four things in sequence; the only second read on it is the People
 * tab's rows, which are paged and filtered and cannot ride on a session.
 *
 * It polls on the run strip's cadence while the session is in progress and stops
 * the moment it is not — {@link useImportSession} reads the answer rather than a
 * state held beside it. And because the rows have no timer of their own, a phase
 * that moves is what refetches them
 * ({@link useImportPhaseInvalidation}), exactly as an observed run state
 * refetches a record.
 *
 * Tabs are route-addressed for the reason the record's are: a link at the end of
 * a commit, or in a message, should land on the surface it is about.
 */

export interface ImportContext {
  importId: string;
  session: ImportSession;
}

export function useImportPage(): ImportContext {
  return useOutletContext<ImportContext>();
}

/**
 * The working line (§1.11), on the same hairlines as the preview and paused
 * lines beside it. It appears only while something is **moving**: the opening
 * turn, the import, the reading of notes. The word is on the head's own line
 * ({@link importHeadLine}); this is the thing the head cannot do, which is pulse.
 */
function ImportStrip({ session, opening }: { session: ImportSession; opening: OpeningTurn }) {
  /* The opening turn is the third kind of work and the only one with no phase
     of its own: the session sits at `profiled` for the two minutes the planner
     is reading. Said here rather than in the conversation, so the pulse is in
     one place on the page. Whether the strip is what the slot holds at all is
     {@link sessionBannerState}'s answer, not this component's — the two used to
     be one question here, and a session paused at its spending limit is the
     case that proved they are not. */
  const opener = openingTurnState(session, opening);
  const line = opener === 'working' ? IMPORT_OPENING_TURN : importProgressLine(session);

  /*
    The same tick the run strip keeps, for the same reason: a session whose
    commit has stalled answers every three seconds with a byte-identical row, so
    structural sharing means no re-render — and the clock beside the line would
    stop with it. It runs only while the session is moving.
  */
  const [, setTick] = useState(0);
  useEffect(() => {
    if (session.phase === null) return;
    const timer = setInterval(() => setTick((value) => value + 1), SILENCE_TICK_MS);
    return () => clearInterval(timer);
  }, [session.phase]);

  if (line === null) return null;

  /* **The heartbeat, not the row's audit stamp.** `updated_at` moves when the
     session row is written and a leg writes it about once a batch, so a commit
     grinding through six hundred rows — or a reading leg spending forty seconds
     a note — sat under a line saying nothing had happened for twelve minutes
     while the counts beside it moved. `progress_at` is stamped by each unit of
     work a leg finishes; it is null on a session that has not done one yet and
     on every session older than the column, where `updated_at` is the same
     answer it always was. */
  const silent = silentMinutes(session.progress_at ?? session.updated_at);

  return (
    <div className="preview-bar working-bar">
      <p className="preview-bar-line">
        <span className="dot" aria-hidden="true" />
        {line}
        {silent !== null ? <span className="silent"> · no signal for {silent}m</span> : null}
      </p>
    </div>
  );
}

/** The three lines the bar has, one per state it renders in. */
const PREVIEW_BAR_LINES = {
  'preview-never': IMPORT_PREVIEW_BAR_NEVER,
  'preview-stale': IMPORT_PREVIEW_BAR_STALE,
  'preview-running': IMPORT_PREVIEW_BAR_RUNNING,
} as const;

/**
 * **One slot under the head, and the one thing that is in it** (§1.11, §2.4).
 *
 * Three things can be: the tinted strip while work is moving, the paused line
 * while the reading is stopped at its spending limit, and the preview bar while
 * the dry run is owed. Which one is {@link sessionBannerState}'s answer and not
 * this component's — a pure rule over the session, asserted as a table, because
 * the ordering is where this went wrong: a paused session keeps `phase` at
 * `reading_notes`, so "is it working" is true of it and the strip pulsed a
 * fraction that had stopped moving over every tab.
 *
 * The preview mutation is held here rather than inside the bar because its
 * `isPending` is one of the rule's inputs; the read-notes mutation is held by the
 * paused line, which is the only thing that presses it.
 */
function ImportBanner({
  importId,
  session,
  opening,
}: {
  importId: string;
  session: ImportSession;
  opening: OpeningTurn;
}) {
  const preview = usePreviewImport(importId);

  /* **The wait is the one banner state that ends without the server saying so.**
     The read polls every three seconds while the phase is non-null, and a
     waiting session answers with a byte-identical row every time — structural
     sharing, no re-render — so both the minute figure on the line and the rule
     that put the line there would freeze at whatever they were when the moment
     was still in the future. The same tick the strip keeps, for the same reason
     and on the same argument: it runs only while there is something counting
     down. */
  const [, setTick] = useState(0);
  const waitingUntil = session.waiting_for_model_until;
  useEffect(() => {
    if (waitingUntil === null) return;
    const timer = setInterval(() => setTick((value) => value + 1), WAITING_TICK_MS);
    return () => clearInterval(timer);
  }, [waitingUntil]);

  /* The opening turn is work with no phase behind it, so the rule cannot see it
     and the page says so on the rule's behalf. */
  const opener = openingTurnState(session, opening);
  const state: SessionBannerState =
    opener === 'working' ? 'working' : sessionBannerState(session, preview.isPending);

  if (state === null) return null;
  if (state === 'working') return <ImportStrip session={session} opening={opening} />;
  if (state === 'paused') return <ImportPausedBar importId={importId} session={session} />;
  if (state === 'waiting') return <ImportWaitingBar session={session} />;
  if (state === 'question')
    return <ImportNextStepBar importId={importId} line={IMPORT_BANNER_QUESTION} />;
  if (state === 'needs-look') {
    return (
      <ImportNextStepBar
        importId={importId}
        line={importBannerNeedsLook(session.counts.needs_a_look)}
        to={{ path: '/people', label: IMPORT_BANNER_TO_PEOPLE, primary: false }}
      />
    );
  }
  if (state === 'ready') {
    return (
      <ImportNextStepBar
        importId={importId}
        line={session.findings.length > 0 ? IMPORT_BANNER_REVIEW_FINDINGS : IMPORT_BANNER_READY}
        to={{ path: '/import', label: IMPORT_BANNER_TO_IMPORT, primary: true }}
      />
    );
  }
  return <ImportPreviewBar preview={preview} line={PREVIEW_BAR_LINES[state]} />;
}

/**
 * **The next step of a session nobody has imported yet** (import UX audit,
 * 2026-10-24), in the slot the preview bar uses: a sentence, and a link to the
 * tab that holds the press. It links rather than presses, because the Import
 * tab is where the notes switch, the findings and the replace diff are decided;
 * the link is left off on the tab it points at, so the page has one way on.
 */
function ImportNextStepBar({
  importId,
  line,
  to,
}: {
  importId: string;
  line: string;
  to?: { path: string; label: string; primary: boolean };
}) {
  /* The same route the tab link points at, so the two cannot disagree. */
  const here = useMatch(`/imports/:importId${to?.path ?? ''}`) !== null && to !== undefined;

  return (
    <div className="preview-bar">
      <div className="preview-bar-line" aria-live="polite">
        {line}
      </div>
      {to && !here ? (
        <div className="preview-bar-actions">
          <Link className={to.primary ? 'btn go' : 'btn act'} to={`/imports/${importId}${to.path}`}>
            {to.label}
          </Link>
        </div>
      ) : null}
    </div>
  );
}

/**
 * **The reading stopped on the model's rate limit** (§1.11) — the same slot, the
 * same left rule, and deliberately nothing to press.
 *
 * The paused bar beside it carries a press, because a session paused at the
 * monthly AI allowance is waiting on a person. This one is waiting on a clock: the leg left every
 * unread note `pending`, wrote down when to come back, and put a successor on
 * the queue for that moment. Nothing anybody can do from here makes a quota lift
 * sooner, and a button that only re-ran the wait would teach the office to press
 * it — so the bar is a sentence, and the sentence says when.
 *
 * **Muted, and that is the whole of the visual difference.** Amber is this
 * page's colour for "somebody is being asked for something", and nobody is being
 * asked for anything here; the tint stays so the slot reads as one family, and
 * the rule and the ink drop back so it does not read as a demand. It is a
 * statement about a wait, not a flag.
 *
 * **And it counts down under a reader standing on it.** The session read polls
 * while the phase is non-null, and `reading_notes` is where a waiting session
 * sits — so the minute figure comes back down on its own, and the bar turns into
 * the strip by the ordinary route the moment the successor leg reads a note.
 */
function ImportWaitingBar({ session }: { session: ImportSession }) {
  return (
    <div className="preview-bar waiting-bar">
      {/* Polite for the reason the two bars beside it are: the number on it
          changes under whoever is reading it, and none of it is urgent. */}
      <div className="preview-bar-line" aria-live="polite">
        {importWaitingBannerLine(session)}
      </div>
    </div>
  );
}

/**
 * **The reading paused at the organization's monthly AI allowance, said where
 * the office is standing** (§1.11, 2026-09-06).
 *
 * A paused session is not a session that is doing something slowly, and that
 * was visible on exactly one of the four tabs while the head said `Reading
 * notes` and the strip pulsed a fraction that would never move again. So the
 * line is in the slot, on every tab: how far the reading got, and that the move
 * is to contact Arbiter, as every monthly-limit refusal says (run defaults
 * sweep, 2026-09-26). An import session has no limit of its own to raise here.
 *
 * **Read the rest** stays, because it is how the reading carries on once the
 * allowance moves; pressed while it has not, the door refuses with the monthly
 * limit's own line. On success the line becomes the strip by the ordinary
 * route: the door queues the next leg, the mutation invalidates the session,
 * and the session comes back with `notes_paused_for_spend` false.
 */
function ImportPausedBar({ importId, session }: { importId: string; session: ImportSession }) {
  const readNotes = useReadImportNotes(importId);

  return (
    <div className="preview-bar paused-bar">
      {/* Polite for the reason the preview bar's line is: it changes under a
          reader who is standing on it, and none of it is urgent. */}
      <div className="preview-bar-line" aria-live="polite">
        {importPausedBannerLine(session)}
      </div>
      <div className="preview-bar-actions paused-bar-actions">
        <WorkingButton
          label={IMPORT_READ_THE_REST}
          workingLabel={IMPORT_READING_THE_REST}
          variant="act"
          working={readNotes.isPending}
          /* `all: false` — the notes this session already queued and did not get
             to, which is what "the rest" means. */
          onClick={() => readNotes.mutate({ all: false })}
        />
      </div>
      {readNotes.isError ? <ErrorLine error={readNotes.error} /> : null}
    </div>
  );
}

/**
 * **The preview bar** (§1.7, §1.11) — the dry run, said where the office is
 * standing rather than on the one tab that held the button.
 *
 * Re-running the preview is the gesture the session turns on: it is what
 * unblocks Commit, and it is what turns an answered duplicate group back into
 * ordinary rows. It was a control inside the People tab's filter row, which
 * meant somebody could edit the plan, answer six namesakes, walk to the Import
 * tab and find a dimmed button with a sentence under it naming a control on a
 * tab they had left.
 *
 * So it is here, under the head, on **every** tab — in the slot the run strip
 * uses, because the two are the same statement about the whole page and they are
 * never both true (see {@link previewBarState} for the guard that says so out
 * loud). It is the only Run preview on the page now: the People tab's and the
 * Import tab's copies came off (import UX audit, 2026-10-24).
 *
 * The mutation is `usePreviewImport`, the same one both other spots use, so its
 * invalidation is the same and a run pressed anywhere clears the bar everywhere:
 * the session comes back with `previewed_at` set and `preview_stale` false, and
 * the bar's own rule stops returning a state. It is passed in rather than made
 * here because whether it is in flight is one of the things the slot's rule
 * reads.
 */
function ImportPreviewBar({
  preview,
  line,
}: {
  preview: ReturnType<typeof usePreviewImport>;
  line: string;
}) {
  return (
    <div className="preview-bar">
      {/* Polite: the line changes under a reader who pressed the button and is
          still standing on it, and none of the three is urgent. */}
      <div className="preview-bar-line" aria-live="polite">
        {line}
      </div>
      <div className="preview-bar-actions">
        {/* Green, because while a preview is owed it is the page's one way on;
            the copies that sat on the People and Import tabs are gone. */}
        <WorkingButton
          label={IMPORT_PREVIEW_RUN}
          workingLabel={IMPORT_PREVIEW_RUNNING}
          working={preview.isPending}
          onClick={() => preview.mutate()}
        />
      </div>
      {preview.isError ? <ErrorLine error={preview.error} /> : null}
    </div>
  );
}

/**
 * **Cancel, in the head on every tab** while the session has written nothing —
 * the server's own rule (`IMPORT_ALREADY_COMMITTED` once Import is pressed). It
 * was a faint line beside Import on the third tab, which is where somebody
 * clearing a duplicate upload looks last and where a slip lands next to the
 * button that writes.
 */
function CancelImport({ importId }: { importId: string }) {
  const cancel = useCancelImport(importId);
  const [confirming, setConfirming] = useState(false);

  return (
    <>
      <DangerLink onClick={() => setConfirming(true)}>{IMPORT_CANCEL}</DangerLink>
      {confirming ? (
        <ConfirmModal
          title={IMPORT_CANCEL}
          line={IMPORT_CANCEL_LINE}
          confirmLabel={IMPORT_CANCEL}
          workingLabel={IMPORT_CANCELLING}
          working={cancel.isPending}
          danger
          onConfirm={() => cancel.mutate(undefined, { onSuccess: () => setConfirming(false) })}
          dismissLabel={IMPORT_KEEP}
          onClose={() => setConfirming(false)}
        >
          {cancel.isError ? <ErrorLine error={cancel.error} /> : null}
        </ConfirmModal>
      ) : null}
    </>
  );
}

/** The run strip's threshold, kept the same so one silence reads one way. */
const SILENT_AFTER_MS = 3 * 60_000;
const SILENCE_TICK_MS = 30_000;

/**
 * **How often the rate-limit wait is re-read.** Faster than the silence tick,
 * because this one is counting *down* to a moment the banner disappears at:
 * thirty seconds would leave "trying again in 1 min" on a page whose successor
 * leg had already started reading, and the whole point of the line is that it is
 * true.
 */
const WAITING_TICK_MS = 10_000;

function silentMinutes(at: string): number | null {
  const stamp = new Date(at).getTime();
  if (Number.isNaN(stamp)) return null;
  const elapsed = Date.now() - stamp;
  if (elapsed < SILENT_AFTER_MS) return null;
  return Math.floor(elapsed / 60_000);
}

/**
 * **The page owes the planner the first turn** (§1.1, §1.2).
 *
 * It used to be the New import door's: upload, open, then navigate — which held
 * a modal for up to two minutes on a gesture whose visible work had finished in
 * seconds. The door closes on the upload now and the turn happens here, where
 * there is a conversation to put it in and a strip line to say it is running.
 *
 * **Fired once, and once is a ref rather than a state.** The read polls every
 * three seconds while a session is moving and the effect re-runs on every render
 * the mutation object changes on, so the arming condition alone is not a guard:
 * it stays true for the whole two minutes the first turn is in flight, and a
 * second `POST /:id/open` in that window is a second paid turn on a transcript
 * that is about to have one. The ref is keyed by session so the page does the
 * right thing when the office moves between two of them without unmounting.
 *
 * Across a **reload** the guard is {@link needsOpeningTurn} itself: the turn is
 * only owed while the transcript is empty and the phase is still `profiled`, and
 * a turn that landed moved both.
 */
function useOpeningTurn(importId: string, session: ImportSession | undefined): OpeningTurn {
  const open = useOpenImport(importId);
  const { mutate } = open;
  const fired = useRef<string | null>(null);
  const armed = session !== undefined && needsOpeningTurn(session);

  useEffect(() => {
    if (!armed || fired.current === importId) return;
    fired.current = importId;
    mutate();
  }, [armed, importId, mutate]);

  return {
    fire: () => mutate(),
    pending: open.isPending,
    failed: open.isError,
    error: open.error,
  };
}

export function ImportSessionPage() {
  const params = useParams();
  const importId = (params['importId'] ?? '').toLowerCase();
  const session = useImportSession(importId);

  useImportPhaseInvalidation(importId, session.data);
  const opening = useOpeningTurn(importId, session.data);

  if (session.isPending) {
    return (
      <div className="column">
        <EmptyLine>
          <Loading />
        </EmptyLine>
      </div>
    );
  }

  if (session.isError) {
    return (
      <div className="column">
        <TopBar parent={{ to: '/imports', label: IMPORTS_TITLE }} />
        <div className="head">
          <div className="titles">
            <div className="title">{isNotFound(session.error) ? NOT_FOUND : 'Not available'}</div>
          </div>
        </div>
        <p className="lede">
          {isNotFound(session.error) ? NOT_FOUND_LINE : loadErrorLine(session.error)}
        </p>
      </div>
    );
  }

  const data = session.data;
  const tab = (path: string, label: string) => (
    <TabLink end={path === ''} to={`/imports/${importId}${path}`}>
      {label}
    </TabLink>
  );

  return (
    <div className="column wide">
      <TopBar parent={{ to: '/imports', label: IMPORTS_TITLE }} here={data.file.filename} />

      {/* **The file is the title** (§2.4). The description was, and it is a
          sentence the office writes for the planner — "the club contact board,
          all groups, minus the ones who resigned" — set at 44px across two
          lines of display face. What identifies a session at a glance, in the
          breadcrumb above it and in the ledger it came from, is the filename;
          the sentence is what the session is *about*, which is a subline. It is
          clamped to two lines rather than truncated at a character count, and
          the whole of it is on the File tab. */}
      <div className="head">
        <div className="titles">
          {/* A step down from the page title every other screen sets: a filename
              is not a heading, it is the name of the thing this page is about,
              and at 44px an ordinary export's name ran to two lines of display
              face above a subline that was the actual sentence. */}
          <div className="title file-title">{data.file.filename}</div>
          <div className="subline clamp-2" title={data.description}>
            {data.description}
          </div>
          {/* The phase rides this line — see `importHeadLine`. */}
          <div className="subline">{importHeadLine(data)}</div>
          {/* **Where the work came from** (§4.2), on a session started again on
              a finished one's file. One line and a link, because that is the
              whole of what it is worth: the plan and the answers carried over
              silently, and the only thing somebody might want is to go back and
              read what the earlier session did. */}
          {/* An attendee list goes into an event's graph, not the record. */}
          {data.target_event ? (
            <div>
              <ImportEventBadge event={data.target_event} />
            </div>
          ) : null}
          {data.retried_from ? (
            <div className="subline src">
              {IMPORT_RETRIED_FROM}{' '}
              <Link className="link" to={`/imports/${data.retried_from}`}>
                {IMPORT_RETRIED_FROM_LINK}
              </Link>
            </div>
          ) : null}
        </div>
        {data.state === 'in_progress' && !data.committed ? (
          <div className="actions-right">
            <CancelImport importId={importId} />
          </div>
        ) : null}
      </div>

      {/* **One slot under the head**, and three things that can be in it: the
          tinted line while something is moving, the paused line while the
          reading is stopped at its spending limit, and the preview bar while the
          dry run is owed. Which one is a rule of its own — see
          {@link sessionBannerState}, and {@link ImportBanner} for why the page
          does not decide it with an order of `if`s here. */}
      <ImportBanner importId={importId} session={data} opening={opening} />

      {/* The session died mid-something and `phase` is null the moment it stops,
          so without this the office would be looking at a page with nothing on
          it saying why. */}
      {data.error ? <div className="src">{data.error}</div> : null}

      <div className="import-session">
        <div className="import-chat-side">
          <div className="import-chat-head">{IMPORT_CHAT_HEAD}</div>
          <ImportChat importId={importId} session={data} opening={opening} />
        </div>

        <div className="import-tabs-side">
          <div className="tabs">
            {tab('', IMPORT_TAB_LABELS.plan)}
            {tab('/people', IMPORT_TAB_LABELS.people)}
            {tab('/import', IMPORT_TAB_LABELS.commit)}
            {tab('/file', IMPORT_TAB_LABELS.file)}
          </div>

          <div className="tab-panel">
            <Outlet context={{ importId, session: data } satisfies ImportContext} />
          </div>
        </div>
      </div>
    </div>
  );
}
